import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Spinner } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import type { AuditEvent } from '@/types/api';
import { formatDateTime } from '../../chart/components';
import { settingsApi } from '../api';

/** Translated name of an audit action; unknown actions show their code. */
function actionLabel(t: TFunction, action: string): string {
  return t(`audit.action.${action}`, { defaultValue: action });
}

function detail(t: TFunction, e: AuditEvent): string {
  const d = (e.data ?? {}) as Record<string, unknown>;
  if (e.action === 'ENTRY_CREATE' && d.source)
    return d.source === 'DICTATION' ? t('audit.dictated') : t('audit.manual');
  if (e.action === 'ENTRY_REJECT' && d.reason) return String(d.reason);
  if (e.action === 'ENTRY_APPROVE_ALL' && d.count !== undefined)
    return t('audit.pieces', { n: d.count });
  if (e.action === 'USER_CREATE' && d.email) return String(d.email);
  if (e.action === 'CHAIR_CREATE' && d.name) return String(d.name);
  if (e.action === 'CLINIC_CREATE' && d.name) return String(d.name);
  return '';
}

/** Who viewed or recorded what, and when (newest first, 100 at a time). */
export function AuditLog() {
  const { t } = useTranslation();
  const [before, setBefore] = useState<string | undefined>();
  const [pages, setPages] = useState<AuditEvent[]>([]);
  const events = useResource(async () => {
    const page = await settingsApi.audit({ limit: 100, before });
    setPages((prev) => (before ? [...prev, ...page] : page));
    return page;
  }, before ?? 'first');

  return (
    <div className="stack">
      {events.error ? <Banner tone="error">{errorMessage(events.error)}</Banner> : null}
      <section className="card">
        <div className="card__head">
          <h2>{t('audit.title')}</h2>
        </div>
        <p className="small muted">{t('audit.subtitle')}</p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('audit.time')}</th>
                <th>{t('audit.user')}</th>
                <th>{t('audit.event')}</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((e) => {
                const extra = detail(t, e);
                return (
                  <tr key={e.id}>
                    <td className="small">{formatDateTime(e.createdAt)}</td>
                    <td>{e.user?.name ?? '—'}</td>
                    <td>
                      {actionLabel(t, e.action)}
                      {extra && <span className="muted small"> · {extra}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {events.loading && <Spinner />}
        {!events.loading && events.data?.length === 100 && (
          <button
            type="button"
            className="btn btn--block"
            style={{ marginTop: 12 }}
            onClick={() => setBefore(pages[pages.length - 1]?.createdAt)}
          >
            {t('audit.older')}
          </button>
        )}
      </section>
    </div>
  );
}
